'use client';

import { useContext } from 'react';
import { FeatureFlagsContext } from './FeatureFlagsProvider';
import { type BooleanFlagKey, featureFlags, type VariantFlagKey, type VariantValuesFor } from './flags';
import { getAllFlagValues } from './cookies';

function useFlagValues() {
  const ctx = useContext(FeatureFlagsContext);
  if (ctx) return ctx;

  // Fallback: read from document.cookie on client, or return defaults
  if (typeof document !== 'undefined') {
    return getAllFlagValues(document.cookie);
  }
  return getAllFlagValues('');
}

export function useFeatureFlag(key: BooleanFlagKey): boolean {
  const flags = useFlagValues();
  const value = flags[key];
  if (typeof value === 'boolean') return value;

  const flag = featureFlags.find((f) => f.key === key);
  return flag?.type === 'boolean' ? flag.defaultValue : false;
}

export function useFeatureFlagVariant<K extends VariantFlagKey>(key: K): VariantValuesFor<K> {
  const flags = useFlagValues();
  const value = flags[key];
  const flag = featureFlags.find((f) => f.key === key);

  if (flag && flag.type === 'variant' && typeof value === 'string' && flag.variants.includes(value as never)) {
    return value as VariantValuesFor<K>;
  }

  if (flag && flag.type === 'variant') {
    return flag.defaultValue as VariantValuesFor<K>;
  }

  return '' as VariantValuesFor<K>;
}
